import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../App';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

// Print-friendly report for one promo campaign.
//
// Opened in a new tab from the Admin Panel's "Print" button per campaign card.
// The page auto-invokes window.print() once the detail resolves so admins can
// pipe it straight to a browser-printed PDF or physical printer without extra
// clicks. Styling is intentionally minimal / black-on-white — this is a
// document, not a dashboard tab.
export default function PromoCampaignPrint() {
  const { campaignId } = useParams();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!campaignId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiFetch(`/admin/promo/campaign/${campaignId}`);
        if (cancelled) return;
        setDetail(data);
        // Small delay so recharts has time to paint before the browser
        // capture snapshot fires.
        setTimeout(() => { try { window.print(); } catch { /* no-op */ } }, 600);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load campaign');
      }
    })();
    return () => { cancelled = true; };
  }, [campaignId]);

  if (error) {
    return <div style={{ padding: 32, fontFamily: 'system-ui', color: '#991b1b' }}>Error: {error}</div>;
  }
  if (!detail) {
    return <div style={{ padding: 32, fontFamily: 'system-ui', color: '#64748b' }}>Loading campaign report…</div>;
  }

  const revoked = detail.codes.filter((c) => c.revoked).length;
  const redeemed = detail.codes.filter((c) => c.redeemed).length;
  const available = detail.total_codes - redeemed - revoked;
  const creditsRedeemed = detail.codes.filter((c) => c.redeemed)
    .reduce((s, c) => s + (c.credits || 0), 0);
  const creditsIssued = detail.codes.reduce((s, c) => s + (c.credits || 0), 0);
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—';
  const fmtShortDate = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';

  return (
    <div className="promo-print" style={{ padding: '32px 40px', fontFamily: 'system-ui, sans-serif', color: '#111', background: '#fff', minHeight: '100vh' }}>
      <style>{`
        @media print {
          @page { margin: 12mm; }
          .no-print { display: none !important; }
          body { background: #fff !important; }
        }
        .promo-print h1, .promo-print h2, .promo-print h3 { margin: 0; }
        .promo-print table { border-collapse: collapse; width: 100%; }
        .promo-print th, .promo-print td { padding: 6px 8px; border-bottom: 1px solid #e5e7eb; text-align: left; font-size: 11px; }
        .promo-print th { background: #f9fafb; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; font-size: 10px; color: #475569; }
      `}</style>

      {/* Print-only toolbar (hidden when actually printing) */}
      <div className="no-print" style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
        <button
          onClick={() => window.print()}
          style={{ padding: '6px 14px', background: '#111', color: '#fff', border: 0, borderRadius: 4, cursor: 'pointer', fontSize: 13 }}
          data-testid="print-btn"
        >
          🖨 Print / Save as PDF
        </button>
        <button
          onClick={() => window.close()}
          style={{ padding: '6px 14px', background: '#fff', color: '#111', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}
        >
          Close
        </button>
      </div>

      {/* Header */}
      <div style={{ borderBottom: '2px solid #111', paddingBottom: 16, marginBottom: 20 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>
          Wrapped In Music · Promo Campaign Report
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 700 }}>{detail.campaign_name}</h1>
        <div style={{ display: 'flex', gap: 20, marginTop: 8, fontSize: 12, color: '#334155', flexWrap: 'wrap' }}>
          <div><b>Tag:</b> <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 3 }}>{detail.campaign_tag}</code></div>
          {detail.influencer_handle && <div><b>Influencer:</b> {detail.influencer_handle}</div>}
          <div><b>Created:</b> {fmtDate(detail.created_at)}</div>
          <div><b>Expires:</b> {fmtDate(detail.expires_at)}</div>
        </div>
      </div>

      {/* Metrics tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 24 }}>
        <MetricTile label="Total codes" value={detail.total_codes} />
        <MetricTile label="Redeemed" value={redeemed} accent="#059669" />
        <MetricTile label="Available" value={available} />
        <MetricTile label="Revoked" value={revoked} accent={revoked ? '#dc2626' : '#111'} />
        <MetricTile label="Redemption %" value={`${detail.redemption_rate}%`} accent="#7c3aed" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24, fontSize: 12 }}>
        <div>
          <b>Credits per code:</b> {detail.credits_per_code}<br />
          <b>Credits issued (total):</b> {creditsIssued.toLocaleString()}
        </div>
        <div>
          <b>Credits redeemed:</b> {creditsRedeemed.toLocaleString()}<br />
          <b>Hard cost realized (~$0.003/cr):</b> ${(creditsRedeemed * 0.003).toFixed(2)}
        </div>
      </div>

      {/* Timeline chart */}
      {detail.timeline_30d && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#64748b', marginBottom: 8 }}>
            Redemptions — last 30 days
          </h3>
          <div style={{ height: 120 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={detail.timeline_30d} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <XAxis
                  dataKey="date"
                  tick={{ fill: '#64748b', fontSize: 9 }}
                  tickFormatter={(d) => (d ? d.slice(5) : '')}
                  interval={4}
                />
                <YAxis tick={{ fill: '#64748b', fontSize: 10 }} allowDecimals={false} />
                <Tooltip formatter={(v) => [`${v} codes`, 'Redeemed']} />
                <Bar dataKey="redeemed_count" fill="#7c3aed" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Codes table */}
      <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#64748b', marginBottom: 8 }}>
        All codes ({detail.codes.length})
      </h3>
      <table>
        <thead>
          <tr>
            <th style={{ width: '30%' }}>Code</th>
            <th>Credits</th>
            <th>Status</th>
            <th>Redeemed at</th>
            <th>Expires</th>
          </tr>
        </thead>
        <tbody>
          {detail.codes.map((c) => {
            const status = c.revoked ? 'revoked' : (c.redeemed ? 'redeemed' : 'available');
            const color = status === 'redeemed' ? '#059669' : (status === 'revoked' ? '#dc2626' : '#111');
            return (
              <tr key={c.code}>
                <td style={{ fontFamily: 'ui-monospace, monospace' }}>{c.code}</td>
                <td>{c.credits}</td>
                <td style={{ color, fontWeight: 600 }}>{status}</td>
                <td>{fmtDate(c.redeemed_at)}</td>
                <td>{fmtShortDate(c.expires_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ marginTop: 40, textAlign: 'center', color: '#94a3b8', fontSize: 10 }}>
        Generated {new Date().toLocaleString()} · Wrapped In Music
      </div>
    </div>
  );
}

function MetricTile({ label, value, accent = '#111' }) {
  return (
    <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 4 }}>
      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent }}>{value}</div>
    </div>
  );
}
